import React, { useState, useEffect } from "react";
import { Container, Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { toastOptions } from "../lib/toastOptions";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../FirebaseConfig";
import { UserAuth } from "../Context";
import { useLocation } from "react-router-dom";

const GoogleFormSubmission = () => {
  const { user } = UserAuth();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [urlParams, setUrlParams] = useState({
    university: "",
    course: "",
    lecturer: "",
    id: ""
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    university: "",
    course: "",
    hearAboutUs: "",
    hearAboutUsOther: "",
    feedback: "",
  });
  const [formConfig, setFormConfig] = useState({
    formUrl: "",
    entryIds: {
      name: "",
      email: "",
      university: "",
      course: "",
      feedback: ""
    }
  });

  // Function to parse entry IDs from Google Form URL
  const parseFormEntryIds = (prefillUrl) => {
    const entryIds = {
      name: "",
      email: "",
      university: "",
      course: "",
      feedback: ""
    };

    if (!prefillUrl) return entryIds;

    try {
      const url = new URL(prefillUrl);
      const params = new URLSearchParams(url.search);
      
      // Parse entry IDs from the prefill URL parameters
      // Map based on your provided example: entry.1849842957=Name, entry.1682321959=Email, etc.
      params.forEach((value, key) => {
        if (key.startsWith('entry.')) {
          const entryId = key;
          
          // Map based on your example prefill URL structure
          if (key === 'entry.1849842957') { // Name field
            entryIds.name = entryId;
          } else if (key === 'entry.1682321959') { // Email field
            entryIds.email = entryId;
          } else if (key === 'entry.632324334') { // Course field
            entryIds.course = entryId;
          } else if (key === 'entry.1956636620') { // Feedback field
            entryIds.feedback = entryId;
          }
          // University entry ID - you'll need to add this if it exists in your form
          // For now, we'll use a placeholder that you can update
          // else if (key === 'entry.XXXXXXXXX') { // University field
          //   entryIds.university = entryId;
          // }
        }
      });
    } catch (error) {
      console.error("Error parsing prefill URL:", error);
    }

    return entryIds;
  };

  // Function to convert viewform URL to formResponse URL
  const getFormResponseUrl = (feedbackUrl) => {
    if (!feedbackUrl) return "";
    
    try {
      // Replace viewform with formResponse
      return feedbackUrl.replace('/viewform', '/formResponse');
    } catch (error) {
      console.error("Error converting form URL:", error);
      return "";
    }
  };

  useEffect(() => {
    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    setUrlParams({
      id: searchParams.get('id') || "",
      university: searchParams.get('university') || "",
      course: searchParams.get('course') || "",
      lecturer: searchParams.get('lecturer') || ""
    });
  }, [location]);

  useEffect(() => {
    const fetchReleaseFormData = async () => {
      if (!urlParams.id) return;

      try {
        // Query releaseForm collection where shortCode == id
        const releaseFormQuery = query(
          collection(db, "releaseForm"),
          where("shortCode", "==", urlParams.id)
        );
        const releaseFormSnapshot = await getDocs(releaseFormQuery);

        if (!releaseFormSnapshot.empty) {
          const releaseFormDoc = releaseFormSnapshot.docs[0];
          const releaseFormData = releaseFormDoc.data();
          
          // Update urlParams with university and course from releaseForm
          setUrlParams(prev => ({
            ...prev,
            university: releaseFormData.university || "",
            course: releaseFormData.course || ""
          }));

          // Parse form configuration from feedbackUrl and prefillUrl
          const feedbackUrl = releaseFormData.feedbackUrl || "";
          const prefillUrl = releaseFormData.prefillUrl || "";
          
          // Parse entry IDs from prefill URL
          const entryIds = parseFormEntryIds(prefillUrl);
          
          // Set form configuration
          setFormConfig({
            formUrl: getFormResponseUrl(feedbackUrl),
            entryIds: entryIds
          });
        }
      } catch (error) {
        console.error("Error fetching release form data:", error);
        toast.error("Failed to load form data", toastOptions);
      }
    };

    fetchReleaseFormData();
  }, [urlParams.id]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setFormData(prev => ({
            ...prev,
            name: data.userName || "",
            email: data.email || "",
            university: urlParams.university || "",
            course: urlParams.course || ""
          }));
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to load user data", toastOptions);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user, urlParams]);

  const handleChange = (e) => {
    if ((e.target.name === "email" && userData?.email) || 
        (e.target.name === "name" && userData?.userName)) {
      return;
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Use the dynamically parsed form URL
    const formURL = formConfig.formUrl;
    
    if (!formURL) {
      toast.error("Form URL not found", toastOptions);
      return;
    }
    
    const formDataToSubmit = new FormData();
    
    // Use the dynamically parsed entry IDs
    if (formConfig.entryIds.name) {
      formDataToSubmit.append(formConfig.entryIds.name, formData.name);
    }
    if (formConfig.entryIds.email) {
      formDataToSubmit.append(formConfig.entryIds.email, formData.email);
    }
    if (formConfig.entryIds.university) {
      formDataToSubmit.append(formConfig.entryIds.university, formData.university);
    }
    if (formConfig.entryIds.course) {
      formDataToSubmit.append(formConfig.entryIds.course, formData.course);
    }
    if (formConfig.entryIds.feedback) {
      formDataToSubmit.append(formConfig.entryIds.feedback, formData.feedback);
    }
    
    fetch(formURL, {
      method: "POST",
      body: formDataToSubmit,
      mode: "no-cors",
    })
      .then(() => toast.success("Form submitted successfully!", toastOptions))
      .catch((error) => toast.warn("Error submitting form!", toastOptions));
  };

  if (loading) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center">
        <div>Loading...</div>
      </Container>
    );
  }

  if (userData?.points < 100) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <p className="fs-4 grey">Your points are less than 100</p>
          <p>You need at least 100 points to access this form.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="p-0">
      <div className="w-100" >
        <div className="text-center mb-4">
          <p className="mt-3 fs-4 grey">Feedback Form</p>
        </div>
        <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
          <Form.Group controlId="name">
            <Form.Control
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
              readOnly={!!userData?.userName}
            />
          </Form.Group>
          <Form.Group controlId="email">
            <Form.Control
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              required
              readOnly={!!userData?.email}
            />
          </Form.Group>
          <Form.Group controlId="university">
            <Form.Control
              type="text"
              name="university"
              placeholder="Your University"
              value={formData.university}
              onChange={handleChange}
              required
              readOnly={!!urlParams.university}
            />
          </Form.Group>
          <Form.Group controlId="course">
            <Form.Control
              type="text"
              name="course"
              placeholder="Your Course"
              value={formData.course}
              onChange={handleChange}
              required
              readOnly={!!urlParams.course}
            />
          </Form.Group>
          
          <Form.Group controlId="feedback">
            <Form.Control
              as="textarea"
              name="feedback"
              placeholder="Your Feedback"
              value={formData.feedback}
              onChange={handleChange}
              rows={4}
            />
          </Form.Group>
          <Button variant="primary" type="submit" className="mt-3">
            Submit Feedback
          </Button>
        </Form>
      </div>
    </Container>
  );
};

export default GoogleFormSubmission;